import { Autocomplete, Box, ListItem, ListItemText } from "@mui/material";
import { useEffect } from "react";
import {
  Controller,
  FieldValues,
  useFormContext,
  Validate,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import CommonTextField from "../../../common/components/functional/commonTextField/CommonTextField";
import { VALID_EMAIL_REGEX } from "../../../common/helpers/regexes";
import { getCoreUserFullName } from "../../../common/utils/users";
import { CustomerUser } from "../types/queries";

type Props = {
  customerUsers?: CustomerUser[];
  handleUserPrefill?: (customerUser: CustomerUser | null) => void;
  userId?: string;
  clearEmailError: () => void;
  validateEmailAddress: Validate<string, FieldValues>;
  prefilledUser: CustomerUser | null;
  isCustomerUser?: boolean;
};

const UserEmailAddress = ({
  customerUsers,
  handleUserPrefill,
  userId,
  clearEmailError,
  validateEmailAddress,
  prefilledUser,
  isCustomerUser,
}: Props) => {
  const { t } = useTranslation();

  const {
    formState: { errors },
    trigger,
    control,
  } = useFormContext();

  useEffect(() => {
    trigger("emailAddress");
  }, [trigger]);

  // Sort the array by firstName
  customerUsers &&
    customerUsers.sort((a, b) =>
      (a.account.firstName || "").localeCompare(b.account.firstName || ""),
    );

  const helperText = isCustomerUser
    ? t("customers:usersNS:existingContactPersonsHelperText")
    : t("customers:usersNS:existingUsersHelperText");

  return (
    <Controller
      name="emailAddress"
      control={control}
      rules={{
        required: true,
        validate: (value, formValues) =>
          validateEmailAddress(value, formValues),
        pattern: {
          value: VALID_EMAIL_REGEX,
          message: t("customers:usersNS:invalidEmail"),
        },
      }}
      render={({ field }) => (
        <Box display="flex" gap="8px" alignItems="flex-start">
          {customerUsers && !userId ? (
            <Autocomplete
              disabled={!!userId || !!prefilledUser}
              freeSolo
              autoSelect
              fullWidth
              value={field.value || null}
              options={customerUsers}
              getOptionLabel={(option) =>
                typeof option === "string"
                  ? option
                  : option.account.emailAddress
              }
              isOptionEqualToValue={(option, value) =>
                option?.account?.emailAddress === value ||
                option?.account?.emailAddress === value?.account?.emailAddress
              }
              onInputChange={() => {
                clearEmailError();
              }}
              clearOnBlur
              clearOnEscape
              selectOnFocus={false}
              onChange={(_, newValue, reason) => {
                if (reason === "blur") {
                  if (typeof newValue !== "object" && handleUserPrefill) {
                    const existingUser = customerUsers.find(
                      (customerUser) =>
                        customerUser.account.emailAddress === newValue,
                    );

                    if (existingUser) {
                      return handleUserPrefill(existingUser as CustomerUser);
                    }

                    field.onChange(newValue);
                  }

                  if (typeof newValue === "object" && handleUserPrefill) {
                    field.onChange(newValue);
                    return handleUserPrefill(newValue as CustomerUser);
                  }
                }

                if (reason === "selectOption") {
                  if (handleUserPrefill) {
                    return handleUserPrefill(newValue as CustomerUser);
                  }
                }

                if (reason === "clear" && handleUserPrefill) {
                  handleUserPrefill(null);
                }

                field.onChange(newValue);
              }}
              renderOption={(props, option) => (
                <ListItem {...props} key={option.account.emailAddress}>
                  <ListItemText>{getCoreUserFullName(option)}</ListItemText>
                </ListItem>
              )}
              renderInput={(params) => {
                return (
                  <CommonTextField
                    {...params}
                    fullWidth
                    error={!!errors.emailAddress}
                    onBlur={() => {
                      trigger("emailAddress");
                    }}
                    helperText={
                      !!errors.emailAddress
                        ? errors.emailAddress?.message?.toString()
                        : helperText
                    }
                  />
                );
              }}
            />
          ) : (
            <CommonTextField
              {...field}
              fullWidth
              error={!!errors.emailAddress}
              onBlur={() => trigger("emailAddress")}
              helperText={
                !!errors.emailAddress
                  ? errors.emailAddress?.message?.toString()
                  : ""
              }
            />
          )}
        </Box>
      )}
    />
  );
};

export default UserEmailAddress;
